import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import translations, { DEFAULT_LANGUAGE, LANGUAGES } from '../i18n/translations';

const STORAGE_KEY = 'uiLanguage';

const LanguageContext = createContext();

export const readStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && LANGUAGES[stored] ? stored : DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
};

const lookup = (dict, key) => key.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), dict);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);

  const setLanguage = useCallback((next) => {
    const value = LANGUAGES[next] ? next : DEFAULT_LANGUAGE;
    setLanguageState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // localStorage unavailable; keep in-memory value only
    }
  }, []);

  // public/index.html ships the Chinese tab title; keep the tab title and lang attribute in step.
  useEffect(() => {
    document.title = language === 'en' ? 'AI Mentor Training System' : 'AI mentor 培訓系統';
    document.documentElement.lang = language === 'en' ? 'en' : 'zh-Hant-TW';
  }, [language]);

  const t = useCallback((key, ...args) => {
    const value = lookup(translations[language], key) ?? lookup(translations[DEFAULT_LANGUAGE], key);
    if (value === undefined) return key;
    return typeof value === 'function' ? value(...args) : value;
  }, [language]);

  const tr = useCallback((traditionalChinese, english) => (
    language === 'en' ? english : traditionalChinese
  ), [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    tr,
    isEnglish: language === 'en',
    speechLanguage: LANGUAGES[language].speech,
    languages: LANGUAGES,
  }), [language, setLanguage, t, tr]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
